import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function OrganizationEntry() {
  const [orgId, setOrgId] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingDomain, setCheckingDomain] = useState(true);
  const [pageConfig, setPageConfig] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 首先檢查當前網域是否有對應的組織
    checkDomainOrganization();
  }, []);

  const checkDomainOrganization = async () => {
    try {
      const currentDomain = window.location.hostname;
      console.log('Checking domain:', currentDomain);
      
      // 檢查是否為自訂網域
      const res = await axios.get(`${API}/organizations/by-domain/${currentDomain}`);
      
      if (res.data.found && res.data.organization) {
        const org = res.data.organization;
        console.log('Found organization for domain:', org.name);
        
        // 儲存組織資訊並直接跳轉到登入頁
        localStorage.setItem('currentOrg', JSON.stringify(org));
        localStorage.setItem('organization_id', org.organization_id);
        navigate(`/login/${org.organization_id}`);
        return;
      }
      
      // 如果沒有找到對應網域的組織，載入入口頁配置
      loadPageConfig();
    } catch (error) {
      console.error('Error checking domain:', error);
      loadPageConfig();
    } finally {
      setCheckingDomain(false);
    }
  };

  const loadPageConfig = async () => {
    try {
      // Use a default org ID or system-wide config
      const res = await axios.get(`${API}/page-config/org_entry?organization_id=system`);
      setPageConfig(res.data);
    } catch (error) {
      console.error('Failed to load page config', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgId.trim()) {
      toast.error('請輸入機構編號');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations/${orgId}`);
      if (res.data) {
        // 檢查是否需要組織ID
        if (res.data.require_org_id === false) {
          toast.info('此組織已設定無需輸入機構編號');
        }
        
        localStorage.setItem('currentOrg', JSON.stringify(res.data));
        localStorage.setItem('organization_id', orgId);
        navigate(`/login/${orgId}`);
      }
    } catch (error) {
      toast.error('機構編號不存在');
    } finally {
      setLoading(false);
    }
  };

  // 顯示載入中狀態
  if (checkingDomain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-600">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">正在載入...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: pageConfig?.background_color || 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)'
    }}>
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1574966390692-5140d4310743?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} />
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            {pageConfig?.logo_url ? (
              <img src={pageConfig.logo_url} alt="Logo" className="h-24 mx-auto mb-4" />
            ) : (
              <div className="w-24 h-24 bg-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            )}
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              {pageConfig?.title || 'AI Investment'}
            </h1>
            <p className="text-gray-600">
              {pageConfig?.subtitle || '請輸入您的機構編號'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                data-testid="org-id-input"
                type="text"
                placeholder="機構編號"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="h-12 text-lg"
              />
            </div>

            <Button 
              data-testid="continue-btn"
              type="submit" 
              className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? '驗證中...' : '繼續'}
            </Button>
          </form>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 text-white/70 text-sm">
          Made with AI Investment
        </div>
      </div>
    </div>
  );
}
